<template>
	<div
		ref="app"
		:class="{
			'dark bg-gray-900 text-gray-200': isDark,
			'bg-white text-gray-900': !isDark,
			'bg-red-900': warnAboutProd && isDark,
			'bg-red-200': warnAboutProd && !isDark,
		}"
		class="appWrapper relative flex h-screen flex-col overflow-hidden dark:text-gray-100 print:!h-auto"
		data-app
	>
		<TheHeader v-model:mirus-controls="mirusControls">
			<template #skipLink>
				<div
					class="absolute left-0 top-1 z-10 grid w-full -translate-y-24 place-content-center overflow-hidden !transition-all focus-within:translate-y-0"
				>
					<a
						ref="skipLink"
						class="m-1 border-2 border-blue-500 bg-white p-5 !text-blue-600 underline dark:bg-gray-800 dark:!text-blue-300"
						href="#main"
					>
						Skip to main content
					</a>
				</div>
			</template>
		</TheHeader>

		<div class="flex flex-1 overflow-hidden">
			<TheSideNav>
				<template #skipLink>
					<a
						ref="navSkipLink"
						href="#main"
						class="absolute left-0 z-40 -translate-y-24 overflow-hidden bg-white px-2 py-5 text-center text-blue-600 !transition-all focus:translate-y-0 dark:bg-gray-800 dark:text-blue-300"
						:class="
							{
								none: 'w-0',
								sm: 'w-16',
								md: 'w-56',
								lg: 'w-64',
								xl: 'w-80',
								full: 'w-full',
							}[!showNavigation ? 'none' : expandTo]
						"
						:inert="!drawerOpen"
					>
						Skip to main content
					</a>
				</template>
			</TheSideNav>
			<div class="grid grid-rows-[auto_1fr]">
				<VueAnnouncer class="sr-only" />
				<AlertList />
				<div class="row-start-2 overflow-y-auto">
					<MirusControls v-if="mirusControls" />
					<DomainProductTypeChangeDialog />
					<SavingChangesSnackbar />
					<div class="flex h-full flex-col justify-between">
						<main id="main" class="z-0 flex-1">
							<TOSUpdate v-if="showTOS" />
							<router-view
								v-if="user || ['logout', 'login'].includes($route.name)"
								ref="routerView"
							/>
						</main>
						<TheFooter />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useDark } from '@vueuse/core';

import useUserStore from '@/stores/user';
import useSupportStore from '@/stores/support';
import useConfigStore from '@/stores/site/config';
import useNotificationStore from '@/stores/site/notifications';
import useNavigationStore from '@/stores/navigation';

import SavingChangesSnackbar from '@/components/MXEditor/SavingChangesSnackbar.vue';

import DomainProductTypeChangeDialog from '@/components/common/DomainProductTypeChangeDialog.vue';

import AlertList from '@/components/common/AlertList';
import TOSUpdate from '@/components/common/TOSUpdate';

import TheFooter from '@/TheFooter';
import TheSideNav from '@/TheSideNav';
import TheHeader from '@/TheHeader';

import MirusControls from '@/components/MirusControls/MirusControls';

const isDark = useDark({ initialValue: 'light', attribute: 'color-scheme' });
const route = useRoute();
const router = useRouter();

const configStore = useConfigStore();
const notificationStore = useNotificationStore();

const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const navigationStore = useNavigationStore();
const { showNavigation, expandTo, drawerOpen, skipLink, navSkipLink } =
	storeToRefs(navigationStore);

const supportStore = useSupportStore();
const { warnAboutProd } = storeToRefs(supportStore);

const showTOS = computed(() => {
	if (!user.value) {
		return false;
	}
	if (user.value.user_type === 'agent' && route.params.agentID) {
		return !user.value.tos_accepted_dashboards.includes(route.params.agentID);
	} else {
		return !user.value.tos_accepted;
	}
});

const mirusControls = computed({
	get: () => Boolean(route?.query?.admin),
	set(value) {
		const { admin, ...rest } = route.query;
		const query = { ...rest };
		if (value) {
			query.admin = 'search';
		}
		router.replace({ query });
	},
});

onMounted(async () => {
	await userStore.getUserData();

	// always check for site config & notifications
	if (userStore?.user?.authenticated) {
		await notificationStore.getSiteNotifications();
	}
	if (!configStore.config) {
		await configStore.getSiteConfig();
	}
});
</script>
<style>
.skipLinkTarget {
	@apply scroll-mt-52 bg-white py-1 text-center text-lg !text-orange-700;
	@apply focus:border-2 focus:border-orange-300 focus:outline-none focus:ring-orange-500;
}
</style>
<style lang="scss">
.bg-checkerboard {
	background-image: url('./assets/images/UI/Transparency500.png');
	@apply bg-cover;
}

p:not(:first-child) {
	@apply mt-2;
}

kbd {
	@apply rounded bg-gray-900 px-1 pb-0.5 pt-1 font-mono font-bold text-white shadow-none dark:bg-gray-200 dark:text-gray-800;
}
.container {
	@apply p-2 lg:p-4 xl:px-16 xl:py-8;
}

a[href] {
	@apply cursor-pointer font-semibold transition-colors;

	@apply inline-block underline decoration-solid decoration-2 underline-offset-2;

	&:not(.inheritColor) {
		&,
		&.infoBlue {
			@apply text-blue-700 hover:text-gray-900 dark:text-blue-200 dark:hover:text-gray-100;
		}
		&.warnYellow {
			@apply text-yellow-700 hover:text-gray-900 dark:text-yellow-100 dark:hover:text-gray-100;
		}
		&.errorRed {
			@apply text-red-700 hover:text-gray-900 dark:text-red-100 dark:hover:text-gray-200;
		}
		&.successGreen {
			@apply text-green-800 hover:text-gray-900 dark:text-green-400 dark:hover:text-gray-100;
		}
		&.mirusWhite {
			@apply text-white hover:text-gray-400 dark:text-gray-300 dark:hover:text-white;
		}
		&.mirusGray {
			@apply text-gray-900 hover:text-orange-900 dark:text-gray-200 dark:hover:text-orange-300;
		}
		&.mirusOrange {
			@apply text-orange-800 hover:text-gray-700 dark:text-orange-300 dark:hover:text-orange-100;
		}
	}
}
</style>

<style lang="scss" scoped>
@import 'styles/variables';
@import 'styles/main';

// increased specificity to override vuetify

div.appWrapper {
	& .link {
		text-decoration: none;
		color: white;
		text-transform: lowercase;
		font-weight: 500;
		font-size: 1.25rem;
		opacity: 1;
		transition: opacity 0.3s ease;
		position: relative;
		&:after {
			content: '';
			position: absolute;
			right: 0;
			bottom: 0;
			height: 1px;
			width: 0;
			transition: width 0.2s ease;
			background-color: white;
		}

		&:hover {
			opacity: 1;
			&:after {
				width: 100%;
			}
		}
		.titleSecond {
			color: black;
			text-transform: uppercase;
			font-size: 0.95rem;
			line-height: 1;
		}
	}

	.headerLabel {
		font-size: 2rem;
		color: white;
		white-space: nowrap;
	}

	.adminLogo {
		cursor: pointer;
		margin-left: 15px;
		opacity: 0.75;
		transition:
			transform 0.3s ease-in-out,
			opacity 0.3s ease;
		&:hover {
			opacity: 1;
		}
	}

	.footerLine {
		height: 2px;
		border: none;
	}
}
</style>
